/**
 * The opt-in account-key fallback offered where a data call was refused for a missing Entra data role (RBAC fix
 * 2026-09-25) — the Azure Portal's "Switch to access key". Asks main whether this identity may read the account keys
 * (`azure-storage:key-access-check`, cached per connection) — a check that never reads a key (review K1): the caller's
 * permissions and the account decide, and `listKeysPermission: 'unknown'` means they could not be read. Accepting saves
 * `azureStorageDataAccess: 'account-key-via-arm'` (+ the resolved resource ID) on the connection and reconnects, after
 * which every open tab refetches on the new connection generation; a switch that fails to connect is put back (the
 * saved record and a reconnect), so a refused key never leaves the connection worse off. The key never reaches the
 * renderer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import type { StorageKeyAccessCheck } from '../../../../types/azureStorage';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { trackStorageEvent } from '../services/storageAnalytics';

export type KeyAccessSurface = 'blobs' | 'entities' | 'messages';
export type KeyAccessOfferState =
  | { kind: 'checking' }
  | { kind: 'unavailable'; reason: string }
  /**
   * `confirmed: false` — the permissions could not be read, so the offer is worded as unconfirmed (review K1);
   * `permissionsError` says whether Azure refused the read or failed (fix-round-A review UXA-03).
   */
  | { kind: 'available'; confirmed: boolean; permissionsError?: 'denied' | 'unavailable'; switching: boolean; failure?: string }
  | { kind: 'dismissed' };

export const storageKeyAccessQueryKey = (connectionId: string) => ['azure-storage', 'key-access-check', connectionId] as const;

async function checkKeyAccess(connectionId: string): Promise<StorageKeyAccessCheck> {
  const result = await window.electronAPI!.azureStorage.keyAccessCheck({ connectionId });
  return result.ok ? result.value : { available: false, code: 'arm-unavailable', reason: result.error.message };
}

/**
 * A switch outlives the offer that started it: the reconnect replaces the tab's listing, which unmounts the offer while
 * it runs, and the offer that mounts afterwards must still show "Switching…" or the failure — so both live per
 * connection here, not in component state.
 */
type SwitchState = { switching: boolean; failure?: string };
const useKeyAccessSwitchStore = create<{ byConnection: Record<string, SwitchState>; set: (connectionId: string, value: SwitchState) => void }>((set) => ({
  byConnection: {},
  set: (connectionId, value) => set((state) => ({ byConnection: { ...state.byConnection, [connectionId]: value } })),
}));
const IDLE: SwitchState = { switching: false };

export function useStorageKeyAccessOffer(connectionId: string, surface: KeyAccessSurface) {
  const query = useQuery({ queryKey: storageKeyAccessQueryKey(connectionId), queryFn: () => checkKeyAccess(connectionId), staleTime: 60_000, retry: false });
  const [dismissed, setDismissed] = useState(false);
  const { switching, failure } = useKeyAccessSwitchStore((state) => state.byConnection[connectionId] ?? IDLE);
  const check = query.data;
  const permission = check?.available && check.listKeysPermission === 'unknown' ? 'unknown' : 'confirmed';
  const shownFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!check || shownFor.current === connectionId) return;
    shownFor.current = connectionId;
    trackStorageEvent('feature:storage_key_access_offer', { action: 'shown', offered: check.available, ...(check.available ? { permission } : { refusal: check.code }), surface });
  }, [check, connectionId, surface, permission]);

  const accept = useCallback(async () => {
    if (!check?.available) return;
    const store = useConnectionStore.getState();
    const saved = store.connections.find((connection) => connection.id === connectionId);
    if (!saved) return;
    const switches = useKeyAccessSwitchStore.getState();
    switches.set(connectionId, { switching: true });
    const next = { ...saved, azureStorageDataAccess: 'account-key-via-arm' as const, azureResourceId: check.resourceId };
    const connected = (await store.updateConnection(next)) && (await store.connectToDatabase(next, { force: true }));
    let failed: string | undefined;
    if (!connected) {
      // Put the connection back the way it was: the error names why the key could not be used (it never carries one).
      const reason = useConnectionStore.getState().connectionErrorById?.[connectionId];
      const restored = (await store.updateConnection(saved)) && (await store.connectToDatabase(saved, { force: true }));
      failed = `${reason ? `${reason.replace(/\.?$/, '.')} ` : ''}${restored ? 'The connection is back on its previous data access.' : 'Switching back failed too — see the connection error in the Object Explorer.'}`;
    }
    switches.set(connectionId, failed ? { switching: false, failure: failed } : IDLE);
    trackStorageEvent('feature:storage_key_access_offer', { action: 'accepted', result: connected ? 'connected' : 'failed', permission, surface });
  }, [check, connectionId, surface, permission]);

  const decline = useCallback(() => {
    setDismissed(true);
    trackStorageEvent('feature:storage_key_access_offer', { action: 'declined', surface });
  }, [surface]);

  const state: KeyAccessOfferState = dismissed ? { kind: 'dismissed' }
    : !check ? { kind: 'checking' }
    : check.available ? { kind: 'available', confirmed: permission === 'confirmed', ...(check.permissionsError ? { permissionsError: check.permissionsError } : {}), switching, failure }
    : { kind: 'unavailable', reason: check.reason };
  return { state, accept, decline };
}
